import React from "react";

import { getIconFont } from "@courses-pm2/icons";

type RibbonProps = {
    /**
     * Specifies the letter which is mapped to the desired icon in the PM2 icon
     * font.
     */
    iconLetter: string;
};

type RibbonState = {
    /**
     * Will specify the font-family name of the pm2 icon font, once it has been
     * fetched and is ready for use.
     */
    fontFamily?: string;
};

/**
 * Renders a triangular ribbon, which will be offset slightly towards the top,
 * left from where it's mounted.
 */
// tslint:disable-next-line variable-name
export class Ribbon extends React.PureComponent<RibbonProps, RibbonState> {
    /**
     * Set up initial state.
     */
    public readonly state: RibbonState = {};

    /**
     * Fetch the icon font as soon as the ribbon is mounted within the DOM.
     */
    public componentDidMount(): void {
        getIconFont()
            .then(
                (fontFamily) => { this.setState({ fontFamily }); },
                () => { /** Silently ignore errors fetching the font! */ },
            );
    }

    /**
     * Primary render method.
     */
    public render(): React.JSX.Element {
        const { iconLetter } = this.props;
        const { fontFamily } = this.state;

        return (
            <span
                className={`ribbon ${fontFamily ? "ribbon--ready" : ""}`}
                style={{
                    fontFamily,
                }}
            >
                <span className="icon">
                    {iconLetter}
                </span>

                <style jsx>{`
                    .ribbon {
                        /** Position in the top/left corner */
                        position: absolute;
                        top: -5px;
                        left: -5px;

                        /** Set up sizing */
                        box-sizing: border-box;
                        width: 70px;
                        height: 70px;

                        /** Disable direct interaction */
                        pointer-events: none;
                        user-select: none;

                        /** Hide icon by default */
                        color: transparent;

                        /** Apply icon styling */
                        font-size: 25px;
                        text-align: center;
                        line-height: 45px;
                        padding-right: 25px;

                        /** Prepare fade-in of the icon */
                        transition: color 0.2s linear;

                        /**
                        * Use the custom icon font to render the icon once available!
                        */
                        &.ribbon--ready {
                            color: #fff;
                        }

                        /** Render triangle using pseudo elements */
                        &::before,
                        &::after {
                            /** Position elements */
                            position: absolute;
                            top: 0;
                            left: 0;

                            /** Render element using borders */
                            border-top: 70px solid transparent;
                            border-right: 70px solid transparent;

                            /** Force rendering */
                            content: "";
                        }

                        &::before {
                            z-index: 2;

                            /** Render the triangle */
                            border-top-color: #dc4320;
                        }

                        &::after {
                            z-index: 1;

                            /**
                            * Move the dropshadow slightly down, so it only overlaps
                            * the parent layer itself
                            */
                            top: 5px;
                            left: 5px;

                            /** Adjust size of the drop shadow */
                            border-top-width: 65px;
                            border-right-width: 65px;

                            /** Render the drop shadow */
                            border-top-color: rgba(0, 0, 0, 0.1);
                        }
                    }

                    .icon {
                        /** Overlap the triangle */
                        position: relative;
                        z-index: 3;
                    }
                `}</style>
            </span>
        );
    }
}
